import { Timestamp } from 'firebase/firestore';

// Social Campaign Management Types

export type CampaignPlatform =
  | 'instagram'
  | 'facebook'
  | 'tiktok'
  | 'twitter'
  | 'linkedin'
  | 'snapchat'
  | 'youtube'
  | 'email'
  | 'sms'
  | 'whatsapp'
  | 'other';

export type CampaignContentType =
  | 'post'
  | 'story'
  | 'reel'
  | 'video'
  | 'carousel'
  | 'ad'
  | 'email'
  | 'sms'
  | 'live'
  | 'other';

export type CampaignStatus = 'draft' | 'active' | 'paused' | 'completed' | 'archived';

export type CampaignMedium = 'social' | 'paid' | 'email' | 'sms' | 'cpc' | 'display' | 'affiliate' | 'referral';

export interface UTMParameters {
  utm_source: string;
  utm_medium: CampaignMedium;
  utm_campaign: string;
  utm_content?: string;
  utm_term?: string;
}

export interface SocialCampaign {
  id: string;
  eventId: string;

  // Campaign Info
  name: string;
  slug: string; // URL-safe campaign identifier for UTM matching
  description?: string;
  status: CampaignStatus;

  // Platform & Content Type
  platform: CampaignPlatform;
  contentType: CampaignContentType;

  // UTM Parameters
  utmParams: UTMParameters;

  // Generated Links
  baseUrl: string;
  trackingLink: string;
  shortLink?: string;

  // Budget & Goals (optional)
  budget?: number;
  currency?: string;
  targetClicks?: number;
  targetConversions?: number;
  targetRevenue?: number;

  // Scheduling
  startDate?: Timestamp;
  endDate?: Timestamp;

  // Notes & Tags
  notes?: string;
  tags?: string[];

  // Real-time tracking metrics (updated by web app)
  clicks?: number;
  conversions?: number;
  totalRevenue?: number;
  lastClickAt?: Timestamp;
  lastConversionAt?: Timestamp;

  // Metadata
  createdBy: string;
  createdByName?: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface CampaignMetrics {
  // Traffic Metrics
  clicks: number;
  uniqueVisitors: number;
  pageViews: number;
  sessions: number;

  // Engagement Metrics
  ticketViews: number;
  ticketAdds: number;
  checkoutStarts: number;

  // Conversion Metrics
  conversions: number;
  revenue: number;
  ticketsSold: number;

  // Calculated Metrics
  clickThroughRate?: number; // clicks / impressions (if impressions provided)
  conversionRate: number; // conversions / clicks * 100
  averageOrderValue: number; // revenue / conversions
  costPerClick?: number; // budget / clicks
  costPerConversion?: number; // budget / conversions
  roi?: number; // ((revenue - budget) / budget) * 100
  revenuePerClick: number; // revenue / clicks

  // Manual Input (from ad platforms)
  impressions?: number;
  spend?: number;
}

export interface CampaignWithMetrics extends SocialCampaign {
  metrics: CampaignMetrics;
}

export interface CampaignAnalytics {
  eventId: string;
  dateRange: {
    start: Date;
    end: Date;
  };

  // Overview
  totalCampaigns: number;
  activeCampaigns: number;
  totalBudget: number;
  totalSpend: number;
  totalRevenue: number;
  overallROI: number;

  // Aggregated Metrics
  totalClicks: number;
  totalConversions: number;
  averageConversionRate: number;

  // Platform Performance
  platformPerformance: Array<{
    platform: CampaignPlatform;
    campaigns: number;
    clicks: number;
    conversions: number;
    revenue: number;
    conversionRate: number;
    roi?: number;
  }>;

  // Content Type Performance
  contentTypePerformance: Array<{
    contentType: CampaignContentType;
    campaigns: number;
    clicks: number;
    conversions: number;
    revenue: number;
    conversionRate: number;
  }>;

  // Top Performing Campaigns
  topCampaigns: CampaignWithMetrics[];

  // Daily Performance
  dailyStats: Array<{
    date: string;
    clicks: number;
    conversions: number;
    revenue: number;
  }>;
}

// Request/Response Types
export interface CreateCampaignRequest {
  eventId: string;
  name: string;
  description?: string;
  platform: CampaignPlatform;
  contentType: CampaignContentType;
  medium?: CampaignMedium;
  budget?: number;
  targetClicks?: number;
  targetConversions?: number;
  startDate?: Date;
  endDate?: Date;
  notes?: string;
  tags?: string[];
}

export interface UpdateCampaignRequest {
  campaignId: string;
  eventId: string;
  updates: Partial<Omit<SocialCampaign, 'id' | 'eventId' | 'createdAt' | 'createdBy'>>;
}

export interface CampaignFilters {
  status?: CampaignStatus[];
  platform?: CampaignPlatform[];
  contentType?: CampaignContentType[];
  dateRange?: {
    start: Date;
    end: Date;
  };
}

// Platform Configuration
export interface PlatformConfig {
  id: CampaignPlatform;
  name: string;
  icon: string;
  color: string;
  supportedContentTypes: CampaignContentType[];
  defaultMedium: CampaignMedium;
}

export const PLATFORM_CONFIGS: PlatformConfig[] = [
  {
    id: 'instagram',
    name: 'Instagram',
    icon: 'mdi:instagram',
    color: '#E4405F',
    supportedContentTypes: ['post', 'story', 'reel', 'carousel', 'ad', 'live'],
    defaultMedium: 'social',
  },
  {
    id: 'facebook',
    name: 'Facebook',
    icon: 'mdi:facebook',
    color: '#1877F2',
    supportedContentTypes: ['post', 'story', 'video', 'carousel', 'ad', 'live'],
    defaultMedium: 'social',
  },
  {
    id: 'tiktok',
    name: 'TikTok',
    icon: 'ic:baseline-tiktok',
    color: '#000000',
    supportedContentTypes: ['video', 'ad', 'live'],
    defaultMedium: 'social',
  },
  {
    id: 'twitter',
    name: 'X (Twitter)',
    icon: 'mdi:twitter',
    color: '#1DA1F2',
    supportedContentTypes: ['post', 'video', 'ad'],
    defaultMedium: 'social',
  },
  {
    id: 'linkedin',
    name: 'LinkedIn',
    icon: 'mdi:linkedin',
    color: '#0A66C2',
    supportedContentTypes: ['post', 'video', 'carousel', 'ad'],
    defaultMedium: 'social',
  },
  {
    id: 'snapchat',
    name: 'Snapchat',
    icon: 'mdi:snapchat',
    color: '#FFFC00',
    supportedContentTypes: ['story', 'ad'],
    defaultMedium: 'social',
  },
  {
    id: 'youtube',
    name: 'YouTube',
    icon: 'mdi:youtube',
    color: '#FF0000',
    supportedContentTypes: ['video', 'ad', 'live'],
    defaultMedium: 'social',
  },
  {
    id: 'email',
    name: 'Email',
    icon: 'mdi:email',
    color: '#EA4335',
    supportedContentTypes: ['email'],
    defaultMedium: 'email',
  },
  {
    id: 'sms',
    name: 'SMS',
    icon: 'mdi:message-text',
    color: '#4CAF50',
    supportedContentTypes: ['sms'],
    defaultMedium: 'sms',
  },
  {
    id: 'whatsapp',
    name: 'WhatsApp',
    icon: 'mdi:whatsapp',
    color: '#25D366',
    supportedContentTypes: ['sms'],
    defaultMedium: 'social',
  },
  {
    id: 'other',
    name: 'Other',
    icon: 'mdi:link-variant',
    color: '#666666',
    supportedContentTypes: ['post', 'video', 'ad', 'other'],
    defaultMedium: 'referral',
  },
];

// Utility function to get platform config
export const getPlatformConfig = (platform: CampaignPlatform): PlatformConfig =>
  PLATFORM_CONFIGS.find((p) => p.id === platform) || PLATFORM_CONFIGS[PLATFORM_CONFIGS.length - 1];

// Utility function to generate UTM campaign name
export const generateCampaignSlug = (name: string): string =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '_')
    .substring(0, 50);

// Utility function to build tracking URL
export const buildTrackingUrl = (baseUrl: string, utmParams: UTMParameters): string => {
  const url = new URL(baseUrl);
  url.searchParams.set('utm_source', utmParams.utm_source);
  url.searchParams.set('utm_medium', utmParams.utm_medium);
  url.searchParams.set('utm_campaign', utmParams.utm_campaign);
  if (utmParams.utm_content) {
    url.searchParams.set('utm_content', utmParams.utm_content);
  }
  if (utmParams.utm_term) {
    url.searchParams.set('utm_term', utmParams.utm_term);
  }
  return url.toString();
};

// Default metrics
export const DEFAULT_CAMPAIGN_METRICS: CampaignMetrics = {
  clicks: 0,
  uniqueVisitors: 0,
  pageViews: 0,
  sessions: 0,
  ticketViews: 0,
  ticketAdds: 0,
  checkoutStarts: 0,
  conversions: 0,
  revenue: 0,
  ticketsSold: 0,
  conversionRate: 0,
  averageOrderValue: 0,
  revenuePerClick: 0,
};
